<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: student.full_name,
                        href: meta.links.student_show,
                    },
                    {
                        name: $t('messages.contacts'),
                        href: null,
                    },
                ]"
            />
        </template>

        <div>
            <div class="font-semibold text-lg">
                {{ $tc('messages.relatives', 2) }}
            </div>

            <div>
                <EmptyState v-if="relatives.length === 0" with-icon with-button>
                    <template v-slot:icon>
                        <svg
                            class="mx-auto h-12 w-12 text-gray-400"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"
                            ></path>
                        </svg>
                    </template>
                    <template v-slot:button>
                        <AppButton
                            element="InertiaLink"
                            :href="meta.links.relatives_create"
                            v-if="meta.can.relatives_create"
                            desired-action
                        >
                            <svg
                                class="-ml-1 mr-2 h-5 w-5"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden="true"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                            {{ $t('messages.create') }}
                        </AppButton>
                    </template>
                </EmptyState>

                <div v-else>
                    <div class="w-full flex space-x-4 justify-end" v-if="meta.can.relatives_create">
                        <AppButton element="inertia-link" :href="meta.links.relatives_create">
                            {{ $t('messages.create') }}
                        </AppButton>
                    </div>
                    <ul class="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <li v-for="relative in relatives" :key="relative.id">
                            <div class="relative col-span-1 flex shadow rounded-md space-x-1 items-start">
                                <div
                                    class="flex-1 flex flex-col px-4 py-2 items-start justify-start border-t border-r border-b border-gray-200 bg-white rounded-r-md truncate"
                                >
                                    <div class="w-full flex justify-between">
                                        <span class="text-sm leading-5">
                                            <inertia-link :href="relative.links.show">
                                                {{ relative.full_name }}
                                            </inertia-link>
                                            <span class="text-sm text-gray-500"
                                                >({{ $t('messages.' + relative.relationship) }})</span
                                            >
                                        </span>
                                        <div class="flex space-x-1">
                                            <InertiaSquareLinkButton
                                                :href="relative.links.show"
                                                action="show"
                                                v-if="relative.can.edit"
                                            />
                                            <InertiaSquareLinkButton
                                                :href="relative.links.edit"
                                                action="edit"
                                                v-if="relative.can.edit"
                                            />
                                            <InertiaSquareDeleteButton
                                                :href="relative.links.delete"
                                                v-if="relative.can.delete"
                                                :confirm-text="$t('messages.are_you_sure?')"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <div class="text-sm text-gray-500">
                                            <div v-if="relative.can_take_student">
                                                <svg
                                                    class="w-4 h-4 inline-block text-green-300"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        fill-rule="evenodd"
                                                        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                                        clip-rule="evenodd"
                                                    ></path>
                                                </svg>
                                                <svg
                                                    class="w-4 h-4 inline-block text-green-300"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"
                                                    ></path>
                                                </svg>
                                                {{ $t('messages.can_take_student') }}
                                            </div>
                                            <div v-else>
                                                <svg
                                                    class="w-4 h-4 inline-block text-red-300"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        fill-rule="evenodd"
                                                        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                                        clip-rule="evenodd"
                                                    ></path>
                                                </svg>
                                                <svg
                                                    class="w-4 h-4 inline-block text-red-300"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"
                                                    ></path>
                                                </svg>
                                                {{ $t('messages.cannot_take_student') }}
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <PhonesList :phones="relative.phones" />
                                    </div>
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        <div class="mt-5">
            <div class="font-semibold text-lg">
                {{ $t('messages.other_contacts') }}
            </div>
        </div>

        <EmptyState v-if="contacts.length === 0" :title="$t('messages.student_has_no_contacts')" with-icon with-button>
            <template v-slot:icon>
                <svg
                    class="mx-auto h-12 w-12 text-gray-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"
                    ></path>
                </svg>
            </template>
            <template v-slot:button>
                <AppButton element="InertiaLink" :href="meta.links.contacts_create" desired-action>
                    <svg
                        class="-ml-1 mr-2 h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                            clip-rule="evenodd"
                        />
                    </svg>
                    {{ $t('messages.create') }}
                </AppButton>
            </template>
        </EmptyState>

        <div v-else>
            <div class="w-full flex space-x-4 justify-end">
                <AppButton element="inertia-link" :href="meta.links.contacts_create">
                    {{ $t('messages.create') }}
                </AppButton>
            </div>
            <ul class="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <li v-for="contact in contacts" :key="contact.id">
                    <div class="relative col-span-1 flex shadow rounded-md space-x-1 items-start">
                        <div
                            class="flex-1 flex flex-col px-4 py-2 items-start justify-start border-t border-r border-b border-gray-200 bg-white rounded-r-md truncate"
                        >
                            <div class="w-full flex justify-between">
                                <span class="text-sm leading-5">
                                    <inertia-link :href="contact.links.show">
                                        {{ contact.full_name }}
                                    </inertia-link>
                                    <span class="text-sm text-gray-500"
                                        >({{ $t('messages.' + contact.relationship) }})</span
                                    >
                                </span>
                                <div class="flex space-x-1">
                                    <InertiaSquareLinkButton :href="contact.links.show" action="show" />
                                    <InertiaSquareLinkButton :href="contact.links.edit" action="edit" />
                                    <InertiaSquareDeleteButton
                                        :href="contact.links.delete"
                                        v-if="contact.can.delete"
                                        :confirm-text="$t('messages.are_you_sure?')"
                                    />
                                </div>
                            </div>
                            <div>
                                <div class="text-sm text-gray-500">
                                    <div v-if="contact.can_take_student">
                                        <svg
                                            class="w-4 h-4 inline-block text-green-300"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                fill-rule="evenodd"
                                                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                                clip-rule="evenodd"
                                            ></path>
                                        </svg>
                                        <svg
                                            class="w-4 h-4 inline-block text-green-300"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"
                                            ></path>
                                        </svg>
                                        {{ $t('messages.can_take_student') }}
                                    </div>
                                    <div v-else>
                                        <svg
                                            class="w-4 h-4 inline-block text-red-300"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                fill-rule="evenodd"
                                                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                                clip-rule="evenodd"
                                            ></path>
                                        </svg>
                                        <svg
                                            class="w-4 h-4 inline-block text-red-300"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"
                                            ></path>
                                        </svg>
                                        {{ $t('messages.cannot_take_student') }}
                                    </div>
                                </div>
                            </div>
                            <div>
                                <PhonesList :phones="contact.phones" />
                            </div>
                        </div>
                    </div>
                </li>
            </ul>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaSquareDeleteButton from '@/components/InertiaSquareDeleteButton.vue';
import AppButton from '@/components/AppButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import PhonesList from '@/Pages/Admin/Relatives/PhonesList.vue';

export default {
    components: {
        PanelPage,
        InertiaSquareDeleteButton,
        AppButton,
        EmptyState,
        InertiaSquareLinkButton,
        PhonesList,
    },
    layout: Layout,
    name: 'Index',
    props: ['student', 'contacts', 'relatives', 'meta'],
    data() {
        return {};
    },
};
</script>
